import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, brl, formatDate, formatDateTime } from "../lib/api";
import { StatusBadge } from "../components/Score";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { copyToClipboard } from "../lib/clipboard";
import { Copy, FilePdf, Link as LinkIcon, QrCode, WhatsappLogo, PencilSimple, ClockCounterClockwise, WarningCircle, Prohibit } from "@phosphor-icons/react";
import PixChargeModal from "../components/PixChargeModal";

export default function SaleDetail() {
  const { saleId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [sale, setSale] = useState(null);
  const [pixInstId, setPixInstId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showLocked, setShowLocked] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/sales/${saleId}`);
    setSale(data);
  };
  useEffect(() => { load(); }, [saleId]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/sales/${saleId}/history`);
      setHistory(data);
      setShowHistory(true);
    } catch { toast.error("Erro ao carregar historico"); }
  };

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const signUrl = `${baseUrl}/assinar/${saleId}`;

  const copyLink = async () => {
    const ok = await copyToClipboard(signUrl);
    ok ? toast.success("Link copiado") : toast.error("Falha ao copiar");
  };

  const chargeWhatsApp = async (inst) => {
    try {
      await api.post(`/whatsapp/charge/${inst.id}`);
      toast.success("Cobranca enviada no WhatsApp");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar WhatsApp"); }
  };

  const registerPayment = async (inst) => {
    try {
      const st = instState(inst);
      await api.post("/payments", { installment_id: inst.id, amount: st.balance || inst.amount, method: "pix" });
      toast.success("Pagamento registrado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // Estado de apresentacao da parcela a partir dos dados ja retornados pelo backend
  // (amount original, balance/saldo aberto, paid_total). Nao altera regras financeiras.
  const instState = (it) => {
    const principal = Number(it.amount || 0);
    const paid = Number(it.paid_total || 0);
    const isPaid = it.status === "paid";
    const balance = isPaid ? 0 : Number(it.balance ?? principal);
    const isPartial = !isPaid && paid > 0;
    let label, badgeCls;
    if (isPaid) { label = "Paga"; badgeCls = "green"; }
    else if (it.status === "partial_late") { label = "Parcial em atraso"; badgeCls = "red"; }
    else if (isPartial) { label = "Parcialmente paga"; badgeCls = "medio"; }
    else if (it.status === "late") { label = "Atrasada"; badgeCls = "red"; }
    else { label = "Pendente"; badgeCls = "neutral"; }
    return { principal, paid, balance, isPaid, isPartial, hasBalance: balance > 0.005, label, badgeCls };
  };

  const doCancelSale = async () => {
    if (!cancelReason.trim()) { toast.error("Informe o motivo do cancelamento"); return; }
    setCancelling(true);
    try {
      await api.post(`/sales/${saleId}/cancel`, { reason: cancelReason.trim(), device: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "" });
      toast.success("Venda cancelada. Estoque devolvido e partes notificadas.");
      setShowCancel(false); setCancelReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao cancelar venda");
      setCancelling(false);
    }
  };

  if (!sale) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]">Carregando...</div>;

  const pdfUrl = sale.promissory ? `${process.env.REACT_APP_BACKEND_URL}/api/promissories/${sale.promissory.id}/pdf` : null;

  return (
    <div data-testid="sale-detail-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Venda</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">#{sale.number} · {sale.client_name}</h1>
          <div className="flex gap-3 mt-2 text-xs text-[#5C5A57]">
            <span>{formatDate(sale.created_at)}</span><span>·</span><StatusBadge status={sale.status} />
          </div>
        </div>
        <div className="text-right">
          <div className="kpi-label">Total da venda</div>
          <div className="font-display text-3xl font-bold tabular">{brl(sale.total)}</div>
          {user?.role !== "client" && (
            <div className="flex items-center gap-3 justify-end mt-3">
              <button onClick={loadHistory} className="text-[10px] tracking-widest uppercase font-medium text-[#5C5A57] hover:underline flex items-center gap-1" data-testid="sale-history-btn">
                <ClockCounterClockwise size={14} /> Historico{sale.edit_history_count ? ` (${sale.edit_history_count})` : ""}
              </button>
              {sale.can_edit && (
                <button onClick={() => nav(`/vendas/${saleId}/editar`)} className="brand" data-testid="edit-sale-btn"><PencilSimple size={14} className="inline mr-1" />Editar venda</button>
              )}
              {sale.edit_locked_by_payment && (
                <button onClick={() => setShowLocked(true)} className="text-[10px] tracking-widest uppercase font-medium text-[#8B5E5E] hover:underline flex items-center gap-1" data-testid="edit-locked-btn"><WarningCircle size={14} /> Editar venda</button>
              )}
              {user?.role === "admin" && sale.status !== "cancelado" && (
                sale.has_paid_installment ? (
                  <button onClick={() => setShowLocked(true)} className="text-[10px] tracking-widest uppercase font-medium text-[#8B5E5E] hover:underline flex items-center gap-1" data-testid="cancel-sale-locked-btn"><Prohibit size={14} /> Cancelar venda</button>
                ) : (
                  <button onClick={() => setShowCancel(true)} className="text-[10px] tracking-widest uppercase font-medium text-[#B4564F] hover:underline flex items-center gap-1" data-testid="cancel-sale-btn"><Prohibit size={14} /> Cancelar venda</button>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {sale.status === "pending_signature" && user?.role !== "client" && (
        <div className="border-2 border-[#0A0A0A] bg-[#F5F0EB] p-6 mb-6">
          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-2">Promissoria pendente de assinatura</div>
          <div className="flex items-center gap-3 flex-wrap">
            <input readOnly value={signUrl} className="input-underline flex-1 min-w-[280px]" data-testid="sign-link-input" />
            <button onClick={copyLink} className="brand" data-testid="copy-sign-link"><Copy size={14} className="inline mr-1" />Copiar link</button>
            <a href={signUrl} target="_blank" rel="noopener noreferrer" className="text-xs tracking-widest uppercase font-medium hover:underline"><LinkIcon size={12} className="inline mr-1" />Abrir</a>
          </div>
        </div>
      )}

      {sale.promissory && (
        <div className="border border-[#EBE5DF] bg-white p-6 mb-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Promissoria digital</div>
            <div className="font-display text-xl font-bold">N. {sale.promissory.number}</div>
            <div className="text-xs text-[#5C5A57] mt-1">Assinada em {formatDateTime(sale.promissory.issued_at)}</div>
            <div className="text-[10px] text-[#5C5A57] mt-1 tabular">Hash {sale.promissory.acceptance_hash?.slice(0, 24)}...</div>
          </div>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="brand" data-testid="download-pdf-btn"><FilePdf size={16} className="inline mr-2" />Baixar PDF</a>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-[#EBE5DF] bg-white p-6 overflow-x-auto">
          <h2 className="section-title mb-4">Produtos</h2>
          <table className="crl">
            <thead><tr><th>Item</th><th>Qtd</th><th>Unit</th><th>Subtotal</th></tr></thead>
            <tbody>
              {sale.products.map((p, i) => (
                <tr key={i}>
                  <td>{p.name}</td>
                  <td className="tabular">{p.qty}</td>
                  <td className="tabular">{brl(p.price)}</td>
                  <td className="tabular font-medium">{brl(p.qty * p.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-[#EBE5DF] bg-white p-6 overflow-x-auto">
          <h2 className="section-title mb-4">Parcelas</h2>
          <table className="crl">
            <thead><tr><th>#</th><th>Vencimento</th><th>Valor</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {(sale.installments || []).map((it) => {
                const st = instState(it);
                return (
                <tr key={it.id}>
                  <td>{it.number}</td>
                  <td className="text-[#5C5A57] text-xs">{formatDate(it.due_date)}</td>
                  <td className="tabular" data-testid={`inst-value-${it.id}`}>
                    {st.isPartial ? (
                      <div className="space-y-0.5 text-xs leading-tight">
                        <div className="text-[#5C5A57]">Original <span className="tabular">{brl(st.principal)}</span></div>
                        <div className="text-[#3F6B45]">Pago <span className="tabular">{brl(st.paid)}</span></div>
                        <div className="font-medium text-black">Saldo <span className="tabular" data-testid={`inst-balance-${it.id}`}>{brl(st.balance)}</span></div>
                      </div>
                    ) : st.isPaid ? (
                      <span className="tabular text-[#5C5A57] line-through">{brl(st.principal)}</span>
                    ) : (
                      <span className="tabular font-medium">{brl(st.principal)}</span>
                    )}
                  </td>
                  <td><span className={`badge ${st.badgeCls}`} data-testid={`inst-status-${it.id}`}>{st.label}</span></td>
                  <td>
                    {!st.isPaid && user?.role !== "client" && (
                      <div className="flex gap-2">
                        <button onClick={() => registerPayment(it)} className="text-[10px] tracking-widest uppercase font-medium hover:underline" data-testid={`mark-paid-${it.id}`}>Marcar pago</button>
                        {st.hasBalance && (
                          <button onClick={() => setPixInstId(it.id)} className="text-[10px] tracking-widest uppercase font-medium text-[#3B6E5E] hover:underline flex items-center gap-1" data-testid={`gen-pix-${it.id}`}><QrCode size={12} /> Gerar PIX</button>
                        )}
                        <button onClick={() => chargeWhatsApp(it)} className="text-[10px] tracking-widest uppercase font-medium text-[#1F8A4C] hover:underline flex items-center gap-1" data-testid={`wa-charge-${it.id}`}><WhatsappLogo size={12} weight="fill" /> Cobrar WhatsApp</button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PixChargeModal
        installmentId={pixInstId}
        open={!!pixInstId}
        onClose={() => setPixInstId(null)}
        onSettled={() => { setPixInstId(null); load(); }}
      />

      {showCancel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="cancel-sale-modal">
          <div className="bg-white max-w-md w-full p-6">
            <div className="flex items-center gap-2 text-[#B4564F] font-medium mb-2"><Prohibit size={20} weight="fill" /> Cancelar venda</div>
            <p className="text-sm text-[#5C5A57] mb-4">
              Esta ação irá: devolver os produtos ao estoque, cancelar as parcelas pendentes, cancelar cobranças PIX e a promissória, zerar o saldo devedor desta venda e notificar cliente e revendedora. A auditoria será registrada.
            </p>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Motivo do cancelamento *</label>
            <textarea rows={3} className="w-full p-3 border border-[#EBE5DF] text-sm mb-4" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Descreva o motivo" data-testid="cancel-reason-input" />
            <div className="flex gap-3">
              <button onClick={() => setShowCancel(false)} className="flex-1 text-xs tracking-widest uppercase font-medium border border-[#EBE5DF] py-3" data-testid="cancel-modal-back-btn">Voltar</button>
              <button onClick={doCancelSale} disabled={cancelling || !cancelReason.trim()} className="flex-1 bg-[#B4564F] text-white text-xs tracking-widest uppercase font-medium py-3 disabled:opacity-50" data-testid="cancel-confirm-btn">{cancelling ? "Cancelando..." : "Confirmar cancelamento"}</button>
            </div>
          </div>
        </div>
      )}

      {showLocked && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="locked-modal">
          <div className="bg-white max-w-md w-full p-6">
            <div className="flex items-center gap-2 text-[#8B5E5E] font-medium mb-2"><WarningCircle size={20} weight="fill" /> Pagamento confirmado</div>
            <p className="text-sm text-[#5C5A57]">Esta venda já possui pagamento confirmado. Para alterar valores, utilize o módulo de <strong>Ajuste Financeiro</strong> (a ser disponibilizado). Nenhum dado financeiro já pago pode ser alterado diretamente.</p>
            <button onClick={() => setShowLocked(false)} className="brand mt-4 w-full" data-testid="locked-close-btn">Entendi</button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="history-modal" onClick={() => setShowHistory(false)}>
          <div className="bg-white max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl font-bold">Historico de alteracoes</h3>
              <button onClick={() => setShowHistory(false)} className="text-[#5C5A57] hover:underline text-xs tracking-widest uppercase" data-testid="history-close-btn">Fechar</button>
            </div>
            {history.length === 0 ? (
              <div className="text-sm text-[#5C5A57]" data-testid="history-empty">Nenhuma alteracao registrada.</div>
            ) : (
              <div className="space-y-4">
                {history.map((h) => (
                  <div key={h.id} className="border border-[#EBE5DF] p-4" data-testid={`history-entry-${h.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="text-sm font-medium">{h.user_name} <span className="text-[10px] tracking-widest uppercase text-[#5C5A57]">({h.user_role})</span></div>
                      <div className="text-xs text-[#5C5A57]">{formatDateTime(h.at)}</div>
                    </div>
                    <div className="text-xs text-[#5C5A57] mb-2">Motivo: <strong>{h.reason}</strong>{h.reason_detail ? ` — ${h.reason_detail}` : ""} · IP {h.ip || "-"}</div>
                    <div className="space-y-1">
                      {(h.changes || []).map((c, i) => (
                        <div key={i} className="text-sm flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] tracking-widest uppercase text-[#5C5A57]">{c.label}:</span>
                          <span className="text-[#8B5E5E] line-through">{String(c.old ?? "-")}</span>
                          <span>→</span>
                          <span className="text-[#3F6B45] font-medium">{String(c.new ?? "-")}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-[#5C5A57] mt-2 border-t border-[#F0EBE5] pt-2">
                      Valor: {brl(h.value_before)} → <strong>{brl(h.value_after)}</strong> ({h.difference >= 0 ? "+" : ""}{brl(h.difference)})
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
